import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, Shield, FileText, Download, Briefcase, ListChecks, Star, Users, CheckCircle2 } from "lucide-react";

const Auth = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const queryParams = new URLSearchParams(window.location.search);
    if (hashParams.get('access_token') || queryParams.get('code')) {
      return;
    }
  }, []);

  useEffect(() => {
    if (user && !authLoading) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        if (error.message.includes("provider is not enabled")) {
          toast.error("Google sign-in is not configured yet. Please contact support.");
        } else {
          toast.error(error.message);
        }
        setLoading(false);
      }
    } catch (error: any) {
      toast.error("An unexpected error occurred");
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-orange-200 rounded-full animate-spin border-t-orange-500" />
            <div className="absolute inset-0 flex items-center justify-center">
              <FileText className="w-6 h-6 text-orange-500" />
            </div>
          </div>
          <p className="text-zinc-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const features = [
    { icon: Shield, label: "Secure Login", color: "from-green-400 to-emerald-500" },
    { icon: FileText, label: "AI Resume Builder", color: "from-orange-400 to-amber-500" },
    { icon: Download, label: "Export to PDF", color: "from-blue-400 to-cyan-500" },
    { icon: Briefcase, label: "Find Jobs", color: "from-purple-400 to-violet-500" },
    { icon: ListChecks, label: "Job Tracker", color: "from-pink-400 to-rose-500" },
  ];

  const testimonials = [
    { name: "Priya S.", role: "Software Engineer", text: "Got 3 interview calls within a week!", rating: 5 },
    { name: "Rahul M.", role: "Marketing Manager", text: "The ATS optimization is incredible.", rating: 5 },
    { name: "Anita K.", role: "Data Analyst", text: "Best resume builder I've ever used.", rating: 5 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-zinc-900 to-slate-900 flex flex-col lg:flex-row overflow-hidden">
      {/* Login Section - Shows first on mobile */}
      <div className="order-1 lg:order-2 lg:w-1/2 flex items-center justify-center p-6 lg:p-16 bg-gradient-to-br from-white via-orange-50/50 to-amber-50/50">
        <div className={`w-full max-w-md transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Login card */}
          <div className="bg-white rounded-3xl shadow-2xl shadow-orange-500/10 p-6 md:p-10">
            <div className="text-center mb-6 md:mb-8">
              <div 
                className={`inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white font-bold text-xl md:text-2xl mb-3 md:mb-4 shadow-lg shadow-orange-500/30 transition-all duration-500 ${mounted ? 'scale-100 rotate-0' : 'scale-0 rotate-180'}`}
                style={{ transitionDelay: '200ms' }}
              >
                ACV
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-zinc-800 mb-1 md:mb-2">Welcome Back</h2>
              <p className="text-zinc-500 text-sm md:text-base">Sign in to continue building your career</p>
            </div>

            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className={`w-full flex items-center justify-center gap-3 px-6 py-3.5 md:py-4 bg-white border-2 border-zinc-200 rounded-2xl hover:border-orange-400 hover:bg-orange-50/50 transition-all duration-300 group shadow-sm hover:shadow-lg hover:shadow-orange-500/10 disabled:opacity-70 disabled:cursor-not-allowed relative overflow-hidden ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: '300ms' }}
            >
              {loading ? (
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-6 h-6 border-2 border-orange-200 rounded-full animate-spin border-t-orange-500" />
                  </div>
                  <span className="font-semibold text-zinc-600">Connecting to Google...</span>
                </div>
              ) : (
                <>
                  <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="font-semibold text-zinc-700 group-hover:text-zinc-900 transition-colors">
                    Continue with Google
                  </span>
                </>
              )}
            </button>

            {/* Security badge */}
            <div 
              className={`mt-4 md:mt-6 flex items-center justify-center gap-2 text-zinc-400 transition-all duration-500 ${mounted ? 'opacity-100' : 'opacity-0'}`}
              style={{ transitionDelay: '400ms' }}
            >
              <Shield className="w-4 h-4 text-green-500" />
              <span className="text-xs font-medium">256-bit SSL Encrypted</span>
            </div>

            {/* Benefits */}
            <div className="mt-6 md:mt-8 space-y-2.5 md:space-y-3">
              {[
                "AI-powered resume suggestions",
                "ATS-optimized templates",
                "One-click PDF export",
                "Job application tracker"
              ].map((benefit, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-3 text-sm text-zinc-600 transition-all duration-500 ${mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                  style={{ transitionDelay: `${500 + index * 100}ms` }}
                >
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 md:mt-6 text-center">
            <p className="text-xs text-zinc-500">
              By continuing, you agree to our{' '}
              <a href="/terms" className="text-orange-600 hover:text-orange-700 underline-offset-2 hover:underline">Terms</a>
              {' '}and{' '}
              <a href="/privacy" className="text-orange-600 hover:text-orange-700 underline-offset-2 hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>

      {/* Features & Social Proof - Shows second on mobile */}
      <div className="order-2 lg:order-1 lg:w-1/2 p-6 lg:p-16 flex flex-col justify-center relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-gradient-to-br from-orange-500/20 to-amber-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-500/20 to-blue-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10">
          {/* Branding */}
          <div className={`mb-8 lg:mb-10 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '100ms' }}>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-3">
              AI CV <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">WALA</span>
            </h1>
            <p className="text-zinc-400 text-base lg:text-lg">
              Build professional, ATS-optimized resumes in minutes
            </p>
          </div>

          {/* Feature badges */}
          <div className="flex flex-wrap gap-2 md:gap-3 mb-8 lg:mb-12">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full group hover:bg-white/10 transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}`}
                style={{ transitionDelay: `${200 + index * 100}ms` }}
              >
                <div className={`p-1 md:p-1.5 rounded-full bg-gradient-to-br ${feature.color}`}>
                  <feature.icon className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" />
                </div>
                <span className="text-xs md:text-sm font-medium text-zinc-300">{feature.label}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className={`grid grid-cols-3 gap-4 md:gap-6 mb-8 lg:mb-12 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '500ms' }}>
            <div className="text-center">
              <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-0.5 md:mb-1">10K+</div>
              <div className="text-zinc-500 text-xs md:text-sm">Active Users</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-0.5 md:mb-1">50K+</div>
              <div className="text-zinc-500 text-xs md:text-sm">Resumes Created</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-0.5 md:mb-1">95%</div>
              <div className="text-zinc-500 text-xs md:text-sm">ATS Pass Rate</div>
            </div>
          </div>

          {/* Testimonials */}
          <div className="space-y-3 md:space-y-4">
            <div className={`flex items-center gap-2 text-zinc-400 text-xs md:text-sm font-medium transition-all duration-500 ${mounted ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '600ms' }}>
              <Users className="w-4 h-4" />
              <span>What our users say</span>
            </div>
            <div className="grid gap-2 md:gap-3">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`p-3 md:p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl md:rounded-2xl hover:bg-white/10 transition-all duration-500 ${mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  style={{ transitionDelay: `${700 + index * 150}ms` }}
                >
                  <div className="flex items-start gap-2 md:gap-3">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-xs md:text-sm flex-shrink-0">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 md:gap-2 mb-0.5 md:mb-1 flex-wrap">
                        <span className="font-semibold text-white text-xs md:text-sm">{testimonial.name}</span>
                        <span className="text-zinc-500 text-xs">• {testimonial.role}</span>
                      </div>
                      <p className="text-zinc-400 text-xs md:text-sm">{testimonial.text}</p>
                      <div className="flex gap-0.5 mt-1.5 md:mt-2">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-3 h-3 md:w-3.5 md:h-3.5 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Loading overlay when authenticating */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl flex flex-col items-center gap-4 max-w-sm mx-4 animate-scale-in">
            <div className="relative">
              <div className="w-16 h-16 md:w-20 md:h-20 border-4 border-orange-200 rounded-full animate-spin border-t-orange-500" />
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-zinc-800 mb-1">Connecting to Google</h3>
              <p className="text-sm text-zinc-500">Please wait while we securely sign you in...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Auth;
